// Silver Sage Homeschool Hub — Class Financials resolver.
//
// Single source of truth for every "how is this class doing financially?"
// number rendered anywhere in the app. Public cards, the Operations Board's
// per-class snapshot, the Class Editor's Overview panel, MAVIS narration, and
// the Operations Board's executive summary all read from here so the numbers
// can never disagree.
//
// Inputs are pulled live from:
//   • enrollment count (class_payments)
//   • billing unit + structured price (classes.billing_unit / price_per_child)
//   • semester calendar (academic_calendar_windows — never hardcode 12 weeks)
//   • instructor pay rules (Hub standard, encoded below)
//
// Nothing here is editable by admins — everything is recomputed from source of
// truth data.

import {
  coerceBillingUnit,
  computeSemesterTotal,
  type BillingUnit,
} from "./classPricing";
import {
  getSemesterMonthCount,
  getSemesterWindowForWeekday,
  type SemesterId,
  type WeekdayName,
} from "./academicCalendar";
import { parseTime, parseWeekdays, type DayName } from "./sessionSchedule";

/**
 * Hub standard instructor pay per class session, keyed by class length.
 *
 *   1 hour    → $30
 *   1.5 hours → $35
 *   2 hours   → $40
 *
 * These three points describe a linear scale (pay = $20 + $10 × hours), which
 * we extrapolate for other lengths so uncommon class lengths still get a
 * sensible number. Rounded to the nearest $0.50.
 */
export function instructorPayPerSession(hours: number): number {
  const h = Number.isFinite(hours) && hours > 0 ? hours : 1;
  const raw = 20 + h * 10;
  return Math.round(raw * 2) / 2;
}

export function parseHoursFromClassLength(text: string | null | undefined): number | null {
  if (!text) return null;
  const t = text.toLowerCase();
  const hr = t.match(/(\d+(?:\.\d+)?)\s*(hours?|hrs?|h)\b/);
  if (hr) return parseFloat(hr[1]);
  const min = t.match(/(\d+)\s*min/);
  if (min) return parseInt(min[1], 10) / 60;
  return null;
}

/** Extract class length in hours from a "10:00 AM – 11:00 AM" style string. */
export function parseHoursFromSchedule(text: string | null | undefined): number | null {
  if (!text) return null;
  if (!parseTime(text)) return null;
  const full = text.match(
    /(\d{1,2}):(\d{2})\s*(AM|PM)?\s*[–\-—]\s*(\d{1,2}):(\d{2})\s*(AM|PM)/i,
  );
  if (!full) return null;
  const endPeriod = full[6].toUpperCase();
  const startPeriod = (full[3] ?? endPeriod).toUpperCase();
  const toMin = (h: string, m: string, p: string) => {
    let hh = parseInt(h, 10);
    if (p === "PM" && hh !== 12) hh += 12;
    if (p === "AM" && hh === 12) hh = 0;
    return hh * 60 + parseInt(m, 10);
  };
  const start = toMin(full[1], full[2], startPeriod);
  let end = toMin(full[4], full[5], endPeriod);
  if (end < start) end += 12 * 60;
  const hours = (end - start) / 60;
  return hours > 0 ? hours : null;
}

/**
 * Everything the resolver needs about a single class. Callers gather this from
 * whatever they already have loaded (DB row, editor draft, static ClassItem).
 */
export interface ClassFinancialInput {
  billingUnit: BillingUnit | string | null | undefined;
  pricePerChild: number | null | undefined;
  hasInstructor: boolean;
  /** True for externally-managed / privately-run classes (no Hub revenue). */
  externalPayment?: boolean;
  /** Free-form schedule string ("Mondays 10:00 AM – 11:00 AM"). */
  scheduleText?: string | null;
  /** Free-form class length string ("1 hour", "90 min"). Optional override. */
  classLengthText?: string | null;
  /** For per_session pricing bundled as N weeks (e.g. "$40 / 4-week session"). */
  sessionLengthWeeks?: number | null;
  /**
   * Price stored on the semester session row (classes.sessions `price`). When
   * set it is exactly what checkout charges per child for the semester, so the
   * forecast must use it instead of deriving from the billing unit.
   */
  semesterPriceOverride?: number | null;
  /** Which semester to price against. Defaults to the current one. */
  semester?: SemesterId;
  enrolled: number;
  capacity?: number | null;
  /** Optional future-session projection inputs. */
  interested?: number | null;
  interestThreshold?: number | null;
}

export interface CapacityProjection {
  capacity: number | null;
  seatsRemaining: number | null;
  potentialGross: number;
  potentialNet: number;
  /** Remaining seats × profit per additional student. */
  potentialAdditional: number;
}

export interface FutureSessionProjection {
  interested: number;
  threshold: number;
  projectedRevenue: number;
  projectedProfit: number;
  unlockMessage: string;
}

export type FinancialHealth = "healthy" | "near" | "below" | "unknown";

export interface FinancialSnapshot {
  applicable: boolean;
  reason?: "external" | "no_instructor" | "no_price" | "no_calendar";
  /** Semester used for the math. */
  semester: SemesterId;
  /** Semester price paid per child (after billing-unit math). */
  perStudentSemester: number;
  instructionalWeeks: number;
  /** Number of actual class meetings across all weekdays this class runs. */
  totalMeetings: number;
  hoursPerSession: number;
  instructorPerSession: number;
  instructorPayroll: number;
  gross: number;
  tithe: number;
  net: number;
  breakEven: number;
  /** Net profit added by every new enrollment past break-even. */
  profitPerStudent: number;
  capacity: CapacityProjection;
  health: FinancialHealth;
  futureSession: FutureSessionProjection | null;
}

const EMPTY_CAPACITY: CapacityProjection = {
  capacity: null,
  seatsRemaining: null,
  potentialGross: 0,
  potentialNet: 0,
  potentialAdditional: 0,
};

const cents = (n: number) => Math.round(n * 100) / 100;

export function computeClassFinancials(input: ClassFinancialInput): FinancialSnapshot {
  const semester: SemesterId = input.semester ?? "fall_2026";
  const billingUnit = coerceBillingUnit(input.billingUnit);

  const base: FinancialSnapshot = {
    applicable: false,
    semester,
    perStudentSemester: 0,
    instructionalWeeks: 0,
    totalMeetings: 0,
    hoursPerSession: 0,
    instructorPerSession: 0,
    instructorPayroll: 0,
    gross: 0,
    tithe: 0,
    net: 0,
    breakEven: 0,
    profitPerStudent: 0,
    capacity: EMPTY_CAPACITY,
    health: "unknown",
    futureSession: null,
  };

  if (input.externalPayment) return { ...base, reason: "external" };
  if (!input.hasInstructor) return { ...base, reason: "no_instructor" };
  const price = Number(input.pricePerChild);
  if (!Number.isFinite(price) || price <= 0) return { ...base, reason: "no_price" };

  // Weeks from the Semester Calendar.
  //   • instructional_weeks — max across the weekdays this class meets. Drives
  //     per-class / per-session pricing math (each child pays for one weekly
  //     stream regardless of how many days the class also happens to run).
  //   • total_meetings — sum across weekdays. Drives payroll (each meeting
  //     gets paid).
  const weekdayNames = parseWeekdays(input.scheduleText ?? "") as DayName[];
  const weekdays = weekdayNames as WeekdayName[];
  let weeks = 0;
  let meetings = 0;
  for (const w of weekdays) {
    const win = getSemesterWindowForWeekday(semester, w);
    if (!win) continue;
    weeks = Math.max(weeks, win.weeks);
    meetings += win.weeks;
  }
  // Weekday couldn't be inferred from schedule text — fall back to the
  // widest configured weekday so we still show meaningful numbers.
  if (weeks === 0) {
    const fallback: WeekdayName[] = [
      "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
    ];
    for (const w of fallback) {
      const win = getSemesterWindowForWeekday(semester, w);
      if (win) {
        weeks = Math.max(weeks, win.weeks);
        break;
      }
    }
    meetings = weeks;
  }
  if (weeks === 0) return { ...base, reason: "no_calendar" };

  const perStudentSemester =
    computeSemesterTotal({
      pricePerChild: price,
      billingUnit,
      instructionalWeeks: weeks,
      semesterMonths: getSemesterMonthCount(semester) ?? undefined,
      sessionLengthWeeks: input.sessionLengthWeeks ?? undefined,
      semesterPriceOverride: input.semesterPriceOverride ?? undefined,
      hasInstructor: true,
    }) ?? 0;

  const hours =
    parseHoursFromClassLength(input.classLengthText) ??
    parseHoursFromSchedule(input.scheduleText) ??
    1;
  const perSession = instructorPayPerSession(hours);
  const payroll = cents(perSession * meetings);

  const enrolled = Math.max(0, Math.floor(input.enrolled ?? 0));
  const gross = cents(perStudentSemester * enrolled);
  const tithe = cents(gross * 0.1);
  const net = cents(gross - tithe - payroll);

  // Every additional enrollment adds (price − 10% tithe) of net profit.
  const profitPerStudent = cents(perStudentSemester * 0.9);
  const breakEven =
    profitPerStudent > 0 ? Math.ceil(payroll / profitPerStudent) : 0;

  const capacity =
    typeof input.capacity === "number" && input.capacity > 0 ? input.capacity : null;
  const seatsRemaining = capacity != null ? Math.max(0, capacity - enrolled) : null;
  const potentialGross =
    capacity != null ? cents(perStudentSemester * capacity) : gross;
  const potentialTithe = cents(potentialGross * 0.1);
  const potentialNet =
    capacity != null ? cents(potentialGross - potentialTithe - payroll) : net;
  const potentialAdditional =
    seatsRemaining != null ? cents(seatsRemaining * profitPerStudent) : 0;

  const health: FinancialHealth =
    enrolled === 0
      ? "below"
      : enrolled >= breakEven + 1
        ? "healthy"
        : enrolled >= Math.max(1, breakEven - 1)
          ? "near"
          : "below";

  let futureSession: FutureSessionProjection | null = null;
  if (
    typeof input.interested === "number" &&
    typeof input.interestThreshold === "number" &&
    input.interestThreshold > 0
  ) {
    const projGross = cents(perStudentSemester * input.interested);
    const projTithe = cents(projGross * 0.1);
    const projNet = cents(projGross - projTithe - payroll);
    const remaining = Math.max(0, input.interestThreshold - input.interested);
    const unlockMessage =
      remaining === 0
        ? "Interest threshold met — ready to open."
        : remaining === 1
          ? "One more family unlocks this class."
          : `${remaining} more families unlock this class.`;
    futureSession = {
      interested: input.interested,
      threshold: input.interestThreshold,
      projectedRevenue: projGross,
      projectedProfit: projNet,
      unlockMessage,
    };
  }

  return {
    applicable: true,
    semester,
    perStudentSemester,
    instructionalWeeks: weeks,
    totalMeetings: meetings,
    hoursPerSession: hours,
    instructorPerSession: perSession,
    instructorPayroll: payroll,
    gross,
    tithe,
    net,
    breakEven,
    profitPerStudent,
    capacity: {
      capacity,
      seatsRemaining,
      potentialGross,
      potentialNet,
      potentialAdditional,
    },
    health,
    futureSession,
  };
}

/** Format an amount as "$120" or "$120.50", dropping trailing .00. */
export function money(n: number): string {
  const r = cents(n);
  return Number.isInteger(r)
    ? `$${r.toLocaleString()}`
    : `$${r.toFixed(2)}`;
}

/** Signed variant for negative payroll/tithe rows: "-$96". */
export function moneySigned(n: number, sign: "+" | "-"): string {
  const label = money(Math.abs(n));
  return `${sign}${label}`;
}

export function healthLabel(h: FinancialHealth): { icon: string; label: string; blurb: string } {
  switch (h) {
    case "healthy":
      return {
        icon: "🟢",
        label: "Healthy",
        blurb: "Enrollment exceeds break-even.",
      };
    case "near":
      return {
        icon: "🟡",
        label: "Near break-even",
        blurb: "One or two more students needed.",
      };
    case "below":
      return {
        icon: "🔴",
        label: "Below break-even",
        blurb: "Instructor payroll exceeds current net revenue.",
      };
    default:
      return { icon: "•", label: "—", blurb: "" };
  }
}
